import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

interface Outcome {
  code: string;
  displayName: string;
  description: string;
  icon: string;
  apps: string[];
  category: string;
  pricing: {
    includedCredits: number;
    trialDays: number;
  };
}

interface WorkspaceOutcome {
  focusCode: string;
  displayName: string;
  status: 'trialing' | 'active' | 'canceled' | 'expired';
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  apps: string[];
}

interface OutcomesTabProps {
  workspaceId: string;
  authToken: string;
}

// SVG icons for outcomes (mapped from Lucide icon names)
const OUTCOME_ICONS: Record<string, JSX.Element> = {
  home: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  users: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  'message-circle': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  'trending-up': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  briefcase: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  calendar: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  'graduation-cap': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  shield: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

/**
 * Outcomes settings tab
 *
 * Features:
 * - Active outcomes with included tools
 * - Add/remove outcomes
 * - Pricing and credits per outcome
 * - Trial status countdown
 */
export function OutcomesTab({ workspaceId, authToken }: OutcomesTabProps) {
  const [enabledOutcomes, setEnabledOutcomes] = useState<WorkspaceOutcome[]>([]);
  const [availableOutcomes, setAvailableOutcomes] = useState<Outcome[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionOutcome, setActionOutcome] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  async function loadData() {
    try {
      const [enabledRes, availableRes] = await Promise.all([
        authFetch(`/api/workspaces/${workspaceId}/focuses`, authToken),
        authFetch('/api/focuses', authToken),
      ]);

      if (enabledRes.ok) {
        const data = await enabledRes.json();
        setEnabledOutcomes(data.focuses || []);
      }

      if (availableRes.ok) {
        const data = await availableRes.json();
        setAvailableOutcomes(data.outcomes || []);
      }
    } catch {
      toast.error('Failed to load outcomes');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAddOutcome(code: string) {
    setActionOutcome(code);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/focuses`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ focusCode: code }),
      });

      if (response.ok) {
        toast.success('Outcome added');
        setShowAddModal(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to add outcome');
      }
    } catch {
      toast.error('Failed to add outcome');
    } finally {
      setActionOutcome(null);
    }
  }

  async function handleRemoveOutcome(code: string) {
    setActionOutcome(code);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/focuses/${code}`, authToken, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Outcome removed');
        setEnabledOutcomes((prev) => prev.filter((o) => o.focusCode !== code));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to remove outcome');
      }
    } catch {
      toast.error('Failed to remove outcome');
    } finally {
      setActionOutcome(null);
    }
  }

  // Get outcomes not yet enabled
  const enabledCodes = new Set(enabledOutcomes.map((o) => o.focusCode));
  const notEnabledOutcomes = availableOutcomes.filter((o) => !enabledCodes.has(o.code));

  // PR-D1: outcomes no longer carry per-outcome pricing — plans price
  // the workspace. Credit contribution remains outcome-scoped.
  const totalCredits = enabledOutcomes.reduce((sum, enabled) => {
    const available = availableOutcomes.find((a) => a.code === enabled.focusCode);
    return sum + (available?.pricing.includedCredits || 0);
  }, 0);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Active Outcomes</h3>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{enabledOutcomes.length}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Included Credits</h3>
            <p className="text-2xl font-semibold text-gray-900 mt-1">
              {totalCredits.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Active Outcomes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Active Outcomes</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Add Outcome
          </button>
        </div>
        <div className="divide-y divide-gray-100">
          {enabledOutcomes.map((outcome) => {
            const details = availableOutcomes.find((a) => a.code === outcome.focusCode);
            const isTrial = outcome.status === 'trialing';

            return (
              <div key={outcome.focusCode} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                      {OUTCOME_ICONS[details?.icon || 'briefcase'] || OUTCOME_ICONS.briefcase}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {outcome.displayName}
                        </h3>
                        {isTrial && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                            Trial
                            {outcome.trialDaysRemaining !== null && (
                              <> &middot; {outcome.trialDaysRemaining}d left</>
                            )}
                          </span>
                        )}
                        {outcome.status === 'active' && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{details?.description}</p>

                      {/* Included tools */}
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-1.5">Included tools:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {outcome.apps.map((app) => (
                            <span
                              key={app}
                              className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 capitalize"
                            >
                              {app}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Credits included — outcomes are not billed
                          per-outcome (PR-D1); plans price the workspace. */}
                      {details && details.pricing.includedCredits > 0 && (
                        <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                          <span>{details.pricing.includedCredits} credits included</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveOutcome(outcome.focusCode)}
                    disabled={actionOutcome === outcome.focusCode}
                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Remove outcome"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
          {enabledOutcomes.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No outcomes enabled yet. Add an outcome to get started.
            </div>
          )}
        </div>
      </div>

      {/* Add Outcome Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Outcome</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {notEnabledOutcomes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  All outcomes are already enabled.
                </div>
              ) : (
                <div className="space-y-4">
                  {notEnabledOutcomes.map((outcome) => (
                    <div
                      key={outcome.code}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
                            {OUTCOME_ICONS[outcome.icon] || OUTCOME_ICONS.briefcase}
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {outcome.displayName}
                            </h4>
                            <p className="text-sm text-gray-500 mt-0.5">{outcome.description}</p>
                            <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                              {outcome.pricing.includedCredits > 0 && (
                                <span>{outcome.pricing.includedCredits} credits</span>
                              )}
                              <span>{outcome.pricing.trialDays}-day trial</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddOutcome(outcome.code)}
                          disabled={actionOutcome === outcome.code}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {actionOutcome === outcome.code ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
